<template>
  <Teleport to="body">
    <div v-if="show" class="dk-agegate" role="dialog" aria-modal="true" :aria-label="String(s.heading ?? 'Age verification')">
      <div class="dk-agegate__card">
        <h2>{{ s.heading || "Are you old enough?" }}</h2>
        <!-- v-html, like the page/article/rich-text renderers: the content is
             the merchant's own, authored in their own dashboard. -->
        <div v-if="!isBlankRichHtml(s.text)" class="dk-rte" v-html="toRichHtml(s.text)" />
        <div class="dk-agegate__actions">
          <button type="button" class="dk-btn" @click="confirm">{{ s.confirm_label || "Yes, I am" }}</button>
          <button type="button" class="dk-agegate__no" @click="deny">{{ s.deny_label || "No, take me away" }}</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { isBlankRichHtml, toRichHtml } from "@dk/theme"
import { computed, onBeforeUnmount, ref, watch } from "vue"
import { isPreview } from "../lib/previewFlag"
import { useBootstrapStore } from "../stores/bootstrap"

const bootstrap = useBootstrapStore()
const s = computed(() => bootstrap.themeSettings.age_gate ?? {})
const KEY = `dk:agegate:${location.host}`

function alreadyConfirmed(): boolean {
  try {
    return localStorage.getItem(KEY) === "1"
  } catch {
    return false
  }
}
const confirmed = ref(alreadyConfirmed())

// Never gate the builder preview — the seller has to be able to see their own
// page, and a modal they must dismiss on every reload would make the editor
// unusable.
const show = computed(() => s.value.enabled === true && !confirmed.value && !isPreview())

// The gate is only honest if the content behind it is not readable, so lock the
// page scroll while it is up.
watch(
  show,
  v => {
    document.body.style.overflow = v ? "hidden" : ""
  },
  { immediate: true },
)
onBeforeUnmount(() => {
  document.body.style.overflow = ""
})

function confirm() {
  try {
    localStorage.setItem(KEY, "1")
  } catch {
    /* storage blocked → they get asked again next visit, which is the safe way round */
  }
  confirmed.value = true
}
function deny() {
  const url = String(s.value.deny_url ?? "").trim()
  // Only ever leave for an absolute URL: a relative path would "send them away"
  // to another page of the very store they just said they cannot enter.
  if (/^https?:\/\//i.test(url)) location.replace(url)
  else history.back()
}
</script>
